import Phaser from 'phaser';

/**
 * BootScene - Handles asset loading and initialization
 */
export class BootScene extends Phaser.Scene {
    constructor() {
        super({ key: 'BootScene' });
    }

    preload(): void {
        // Create loading bar
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;

        const progressBar = this.add.graphics();
        const progressBox = this.add.graphics();
        progressBox.fillStyle(0x222222, 0.8);
        progressBox.fillRect(width / 2 - 160, height / 2 - 25, 320, 50);

        const loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#ffffff',
        });
        loadingText.setOrigin(0.5, 0.5);

        const percentText = this.add.text(width / 2, height / 2, '0%', {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#ffffff',
        });
        percentText.setOrigin(0.5, 0.5);

        // Update progress bar
        this.load.on('progress', (value: number) => {
            percentText.setText(`${Math.round(value * 100)}%`);
            progressBar.clear();
            progressBar.fillStyle(0x00ff00, 1);
            progressBar.fillRect(width / 2 - 150, height / 2 - 15, 300 * value, 30);
        });



        // Load game assets
        this.load.on('complete', () => {
            progressBar.destroy();
            progressBox.destroy();
            loadingText.destroy();
            percentText.destroy();
        });

        // Load placeholder assets (will be replaced with real assets later)
        this.createPlaceholderTextures();
    }

    create(): void {
        // Transition to menu
        this.scene.start('MenuScene');
    }

    /**
     * Creates placeholder textures for development
     * These will be replaced with actual sprites later
     */
    private createPlaceholderTextures(): void {
        // Player placeholder (colored circle)
        const playerGraphics = this.make.graphics({ x: 0, y: 0 });
        playerGraphics.fillStyle(0x4ecdc4, 1);
        playerGraphics.fillCircle(16, 16, 14);
        playerGraphics.lineStyle(2, 0x2a9d8f);
        playerGraphics.strokeCircle(16, 16, 14);
        playerGraphics.generateTexture('player', 32, 32);
        playerGraphics.destroy();

        // Wall tile placeholder
        const wallGraphics = this.make.graphics({ x: 0, y: 0 });
        wallGraphics.fillStyle(0x6c5ce7, 1);
        wallGraphics.fillRect(0, 0, 32, 32);
        wallGraphics.lineStyle(1, 0x5849c4);
        wallGraphics.strokeRect(0, 0, 32, 32);
        wallGraphics.generateTexture('wall', 32, 32);
        wallGraphics.destroy();

        // Floor tile placeholder
        const floorGraphics = this.make.graphics({ x: 0, y: 0 });
        floorGraphics.fillStyle(0x2d3436, 1);
        floorGraphics.fillRect(0, 0, 32, 32);
        floorGraphics.lineStyle(1, 0x404040);
        floorGraphics.strokeRect(0, 0, 32, 32);
        floorGraphics.generateTexture('floor', 32, 32);
        floorGraphics.destroy();

        // Spawn point placeholder
        const spawnGraphics = this.make.graphics({ x: 0, y: 0 });
        spawnGraphics.fillStyle(0x00b894, 0.5);
        spawnGraphics.fillRect(0, 0, 32, 32);
        spawnGraphics.generateTexture('spawn', 32, 32);
        spawnGraphics.destroy();

        // Create all fart textures
        this.createFartTextures();

        // Create all pickup textures
        this.createPickupTextures();
    }

    /**
     * Creates textures for all fart types
     */
    private createFartTextures(): void {
        // === BASIC FART ===
        // Simple yellow cloud
        const basicFart = this.make.graphics({ x: 0, y: 0 });
        basicFart.fillStyle(0xfdcb6e, 0.8);
        basicFart.fillCircle(16, 16, 12);
        basicFart.generateTexture('fart_basic', 32, 32);
        basicFart.destroy();

        // === LINGERING FART ===
        // Organic blob with wavy edges (multiple overlapping circles)
        const lingeringFart = this.make.graphics({ x: 0, y: 0 });
        lingeringFart.fillStyle(0xc8d6a0, 0.6);
        lingeringFart.fillCircle(24, 24, 18);    // Center blob
        lingeringFart.fillCircle(16, 18, 8);     // Top-left bump
        lingeringFart.fillCircle(32, 20, 10);    // Top-right bump
        lingeringFart.fillCircle(18, 32, 9);     // Bottom-left bump
        lingeringFart.fillCircle(30, 30, 7);     // Bottom-right bump
        lingeringFart.generateTexture('fart_lingering', 48, 48);
        lingeringFart.destroy();

        // === GHOST FART ===
        // Wispy ghost shape with subtle eyes
        const ghostFart = this.make.graphics({ x: 0, y: 0 });
        // Main body
        ghostFart.fillStyle(0xdfe6e9, 0.5);
        ghostFart.fillEllipse(20, 18, 16, 18);
        // Wispy tail (3 bumps at bottom)
        ghostFart.fillCircle(12, 34, 7);
        ghostFart.fillCircle(20, 36, 8);
        ghostFart.fillCircle(28, 33, 6);
        // Subtle eyes
        ghostFart.fillStyle(0x636e72, 0.4);
        ghostFart.fillCircle(15, 14, 3);
        ghostFart.fillCircle(25, 14, 3);
        ghostFart.generateTexture('fart_ghost', 40, 44);
        ghostFart.destroy();

        // === EXPLOSIVE FART - WARNING RING ===
        // Pulsing danger ring (shown during charge)
        const explosiveWarning = this.make.graphics({ x: 0, y: 0 });
        explosiveWarning.lineStyle(4, 0xff6b6b, 0.6);
        explosiveWarning.strokeCircle(32, 32, 28);
        explosiveWarning.lineStyle(2, 0xffcc00, 0.4);
        explosiveWarning.strokeCircle(32, 32, 20);
        explosiveWarning.generateTexture('fart_explosive_warning', 64, 64);
        explosiveWarning.destroy();

        // === EXPLOSIVE FART - EXPLOSION ===
        // Spiky starburst
        const explosiveFart = this.make.graphics({ x: 0, y: 0 });
        // Core
        explosiveFart.fillStyle(0xf39c12, 0.9);
        explosiveFart.fillCircle(32, 32, 16);
        // Outer spikes (8 points)
        explosiveFart.fillStyle(0xff6b6b, 0.8);
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const x = 32 + Math.cos(angle) * 26;
            const y = 32 + Math.sin(angle) * 26;
            explosiveFart.fillCircle(x, y, 8);
        }
        // Inner highlights
        explosiveFart.fillStyle(0xffeaa7, 0.7);
        explosiveFart.fillCircle(32, 32, 8);
        explosiveFart.generateTexture('fart_explosive', 64, 64);
        explosiveFart.destroy();

        // === SBD FART (Silent But Deadly) ===
        // Nearly invisible - just a faint shimmer
        const sbdFart = this.make.graphics({ x: 0, y: 0 });
        sbdFart.fillStyle(0xffffff, 0.08);
        sbdFart.fillCircle(12, 12, 10);
        sbdFart.lineStyle(1, 0xffffff, 0.12);
        sbdFart.strokeCircle(12, 12, 10);
        // Subtle inner ring for minimal visibility
        sbdFart.lineStyle(1, 0xffffff, 0.06);
        sbdFart.strokeCircle(12, 12, 6);
        sbdFart.generateTexture('fart_sbd', 24, 24);
        sbdFart.destroy();

        // === PROPULSION FART ===
        // Directional thrust cloud with speed lines
        const propulsionFart = this.make.graphics({ x: 0, y: 0 });
        // Main thrust cloud (elongated)
        propulsionFart.fillStyle(0x8b4513, 0.7);
        propulsionFart.fillEllipse(16, 28, 12, 20);
        propulsionFart.fillStyle(0xa0522d, 0.6);
        propulsionFart.fillEllipse(16, 20, 8, 12);
        // Speed lines
        propulsionFart.lineStyle(2, 0xd2b48c, 0.5);
        propulsionFart.lineBetween(6, 8, 6, 24);
        propulsionFart.lineBetween(16, 4, 16, 20);
        propulsionFart.lineBetween(26, 8, 26, 24);
        propulsionFart.generateTexture('fart_propulsion', 32, 48);
        propulsionFart.destroy();
    }

    /**
     * Creates textures for all food pickup types
     */
    private createPickupTextures(): void {
        // === BEANS (grants Lingering) ===
        // Brown can shape
        const beans = this.make.graphics({ x: 0, y: 0 });
        beans.fillStyle(0x8b4513, 1);
        beans.fillRoundedRect(4, 6, 16, 18, 3);
        // Can top
        beans.fillStyle(0xa0522d, 1);
        beans.fillRoundedRect(3, 4, 18, 4, 2);
        // Label stripe
        beans.fillStyle(0xc8d6a0, 0.9);
        beans.fillRect(6, 10, 12, 6);
        beans.generateTexture('pickup_beans', 24, 28);
        beans.destroy();

        // === MYSTERY MEAT (grants Ghost) ===
        // Suspicious grey-green steak
        const meat = this.make.graphics({ x: 0, y: 0 });
        // Main meat body
        meat.fillStyle(0x95a5a6, 1);
        meat.fillEllipse(12, 14, 10, 8);
        // Bone
        meat.fillStyle(0xecf0f1, 1);
        meat.fillCircle(20, 8, 4);
        meat.fillRect(16, 6, 6, 4);
        // Suspicious green spots
        meat.fillStyle(0x27ae60, 0.6);
        meat.fillCircle(8, 12, 2);
        meat.fillCircle(14, 16, 2);
        meat.generateTexture('pickup_mystery_meat', 24, 24);
        meat.destroy();

        // === HOT PEPPER (grants Explosive) ===
        // Red chili pepper
        const pepper = this.make.graphics({ x: 0, y: 0 });
        // Main body
        pepper.fillStyle(0xe74c3c, 1);
        pepper.fillEllipse(12, 16, 7, 12);
        pepper.fillCircle(12, 6, 5);
        // Stem
        pepper.fillStyle(0x27ae60, 1);
        pepper.fillRect(10, 0, 4, 5);
        // Highlight
        pepper.fillStyle(0xec7063, 0.6);
        pepper.fillEllipse(10, 14, 3, 6);
        pepper.generateTexture('pickup_hot_pepper', 24, 28);
        pepper.destroy();

        // === EGG (grants SBD) ===
        // White egg with yolk hint
        const egg = this.make.graphics({ x: 0, y: 0 });
        egg.fillStyle(0xfdfefe, 1);
        egg.fillEllipse(12, 14, 10, 12);
        // Yolk peek (bottom)
        egg.fillStyle(0xf1c40f, 0.4);
        egg.fillEllipse(12, 16, 6, 5);
        // Subtle shadow/depth
        egg.lineStyle(1, 0xbdc3c7, 0.5);
        egg.strokeEllipse(12, 14, 10, 12);
        egg.generateTexture('pickup_egg', 24, 28);
        egg.destroy();

        // === FIBER SALAD (grants Propulsion) ===
        // Green salad in a bowl
        const salad = this.make.graphics({ x: 0, y: 0 });
        // Bowl
        salad.fillStyle(0xf4d03f, 1);
        salad.fillEllipse(12, 18, 11, 7);
        salad.fillStyle(0x2d3436, 1);
        salad.fillEllipse(12, 18, 8, 4);
        // Leafy greens spilling out
        salad.fillStyle(0x27ae60, 1);
        salad.fillCircle(8, 12, 5);
        salad.fillCircle(16, 11, 5);
        salad.fillCircle(12, 9, 4);
        salad.fillStyle(0x2ecc71, 0.8);
        salad.fillCircle(10, 10, 3);
        salad.fillCircle(14, 10, 3);
        salad.generateTexture('pickup_fiber_salad', 24, 26);
        salad.destroy();

        // === GENERIC POWERUP (legacy, keep for compatibility) ===
        const powerupGraphics = this.make.graphics({ x: 0, y: 0 });
        powerupGraphics.fillStyle(0xe17055, 1);
        powerupGraphics.fillCircle(12, 12, 10);
        powerupGraphics.lineStyle(2, 0xd63031);
        powerupGraphics.strokeCircle(12, 12, 10);
        powerupGraphics.generateTexture('powerup', 24, 24);
        powerupGraphics.destroy();
    }
}
